
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../store';
import { RequestStatus, UserRole } from '../types';
import { 
  MessageSquare, CheckCircle, Clock, MapPin, 
  DollarSign, Send, ChevronLeft, Star, Navigation, 
  Check, Info, User, Hammer, Ruler
} from 'lucide-react';

const RequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    requests, questions, interests, currentUser, 
    updateRequestStatus, addQuestion, answerQuestion, expressInterest, completeJob
  } = useApp();
  const [questionText, setQuestionText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');

  const request = requests.find(r => r.id === id);
  if (!request) return <div className="p-12 text-center font-serif text-amber-900">Request not found in the workshop.</div>;

  const requestQuestions = questions.filter(q => q.requestId === id);
  const requestInterests = interests.filter(i => i.requestId === id);
  const isOwner = currentUser?.id === request.requesterId;
  const isArtisan = currentUser?.role === UserRole.ARTISAN;
  const isAssignedToCurrent = request.assignedArtisanId === currentUser?.id;

  const handlePostQuestion = (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionText || !currentUser) return;
    addQuestion(request.id, currentUser.id, currentUser.name, questionText);
    setQuestionText('');
  };

  const handlePostAnswer = (e: React.FormEvent, questionId: string) => {
    e.preventDefault();
    if (!answerText) return;
    answerQuestion(questionId, answerText);
    setAnswerText('');
    setActiveQuestionId(null);
  };

  const handleLockArtisan = (artisanId: string) => {
    updateRequestStatus(request.id, RequestStatus.LOCKED, artisanId);
  };

  const handleUpdateStatus = (status: RequestStatus) => {
    updateRequestStatus(request.id, status);
  };

  const statusSteps = [
    RequestStatus.POSTED,
    RequestStatus.LOCKED,
    RequestStatus.EN_ROUTE,
    RequestStatus.ARRIVED,
    RequestStatus.COMPLETED
  ];

  const currentStepIndex = statusSteps.indexOf(request.status);

  return (
    <div className="space-y-6 pb-24 max-w-2xl mx-auto px-4 reveal">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <button 
          onClick={() => navigate(-1)} 
          className="p-3 bg-white border border-stone-200 rounded-xl text-stone-600 hover:text-amber-800 hover:border-amber-200 transition-all shadow-sm"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h2 className="text-2xl font-black text-[#2d1b0f] font-serif">Project Ledger</h2>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-stone-400">Reference: {request.id.toUpperCase()}</p>
        </div>
      </div>

      {/* Status Timeline - Wood Plank Style */}
      <div className="bg-[#3d2b1f] p-8 rounded-[2rem] shadow-xl border border-amber-900/30 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-full h-full wood-grain opacity-5 pointer-events-none"></div>
        <div className="flex justify-between items-center mb-10 px-2 relative z-10">
          {statusSteps.map((s, idx) => (
            <div key={s} className="flex flex-col items-center flex-1 relative">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-all z-10 ${
                idx <= currentStepIndex 
                  ? 'bg-amber-600 border-amber-400 text-white shadow-[0_0_15px_rgba(217,119,6,0.4)]' 
                  : 'bg-stone-800 border-stone-700 text-stone-600'
              }`}>
                {idx < currentStepIndex ? <Check size={18} /> : <span className="text-xs font-black">{idx + 1}</span>}
              </div>
              <span className={`text-[8px] font-black uppercase tracking-tighter mt-3 text-center absolute -bottom-6 w-16 ${
                idx <= currentStepIndex ? 'text-amber-400' : 'text-stone-700'
              }`}>{s}</span>
              {idx < statusSteps.length - 1 && (
                <div className={`absolute top-5 left-1/2 w-full h-[3px] -z-0 ${
                  idx < currentStepIndex ? 'bg-amber-600' : 'bg-stone-800'
                }`}></div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-white/5 text-center">
          <p className="text-xs font-black text-amber-100/30 uppercase tracking-[0.4em] mb-1">Current Milestone</p>
          <p className="text-2xl font-black text-amber-50 uppercase tracking-tighter font-serif">{request.status}</p>
        </div>
      </div>

      {/* Project Specs */}
      <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-stone-100 space-y-8 relative overflow-hidden group">
        <div className="flex justify-between items-start relative z-10">
          <div className="space-y-2">
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-700 bg-amber-50 px-3 py-1 rounded-lg border border-amber-100">{request.category}</span>
            <h1 className="text-3xl font-black text-[#2d1b0f] pt-1 font-serif leading-tight">Project Commission</h1>
            <div className="flex items-center gap-2 text-stone-400">
               <User size={14} />
               <span className="text-xs font-bold">{request.requesterName}</span>
            </div>
          </div>
          <div className="bg-[#4a2c2a] text-amber-50 px-6 py-4 rounded-2xl flex flex-col items-center shadow-xl border border-black/20">
            <span className="text-[9px] uppercase font-black tracking-widest text-amber-200/40">Appraisal</span>
            <span className="text-xl font-black font-serif italic">{request.estimatedPrice}</span>
          </div>
        </div>

        <div className="bg-[#faf7f2] p-8 rounded-3xl relative overflow-hidden border border-stone-100 shadow-inner">
          <div className="absolute top-0 left-0 w-full h-full wood-grain opacity-[0.03] pointer-events-none"></div>
          <p className="text-[#2d1b0f] leading-relaxed font-medium italic relative z-10 text-lg">"{request.text}"</p>
          {request.mediaUrl && (
            <div className="mt-6 rounded-2xl overflow-hidden border-4 border-white shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500">
              <img src={request.mediaUrl} alt="Visual Context" className="w-full h-64 object-cover" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 text-[10px] font-black uppercase tracking-widest text-stone-400 border-t border-stone-50 pt-6">
          <div className="flex items-center gap-2 px-4 py-2 bg-stone-50 rounded-xl"><Clock size={16} className="text-amber-700" /> {request.urgency}</div>
          <div className="flex items-center gap-2 px-4 py-2 bg-stone-50 rounded-xl"><MapPin size={16} className="text-rose-700" /> {request.location?.address.split(',')[0]}</div>
        </div>
      </div>

      {/* Artisan Selection (For Requester) */}
      {isOwner && request.status === RequestStatus.POSTED && (
        <div className="space-y-4 reveal-delay-1">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-xl font-black text-[#2d1b0f] font-serif flex items-center gap-2">
              <Hammer size={20} className="text-amber-700" />
              Qualified Masters
            </h3>
            <span className="text-[10px] font-black text-amber-900 bg-amber-100 px-3 py-1 rounded-lg border border-amber-200">{requestInterests.length} Interested</span>
          </div>
          
          <div className="space-y-3">
            {requestInterests.length === 0 ? (
              <div className="bg-white/50 p-12 rounded-[2rem] text-center border-2 border-dashed border-stone-200">
                <div className="w-12 h-12 bg-stone-50 rounded-full flex items-center justify-center mx-auto mb-4 text-stone-300 animate-pulse">
                   <Ruler size={24} />
                </div>
                <p className="text-sm text-stone-400 font-bold uppercase tracking-widest italic">Awaiting Master Applications...</p>
              </div>
            ) : (
              requestInterests.map(interest => (
                <div key={interest.id} className="bg-white p-6 rounded-3xl border border-stone-100 shadow-sm flex items-center justify-between hover:border-amber-400 transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center text-stone-400 group-hover:bg-amber-900 group-hover:text-amber-100 transition-colors">
                      <User size={28} />
                    </div>
                    <div>
                      <h4 className="font-black text-[#2d1b0f] text-lg font-serif">{interest.artisanName}</h4>
                      <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest">
                        <span className="flex items-center gap-1 text-amber-500"><Star size={14} fill="currentColor" /> 4.9</span>
                        <span className="text-stone-200">|</span>
                        <span className="text-stone-400">0.8 mi</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleLockArtisan(interest.artisanId)}
                    className="bg-[#3d2b1f] text-amber-50 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-[0.2em] hover:bg-[#2d1b0f] shadow-lg active:scale-95 transition-all border-b-4 border-black/40"
                  >
                    Lock Job
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Artisan Action Panel */}
      {isAssignedToCurrent && request.status !== RequestStatus.COMPLETED && (
        <div className="bg-[#2d1b0f] text-amber-50 p-8 rounded-[2.5rem] shadow-2xl space-y-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-full h-full wood-grain opacity-10 pointer-events-none"></div>
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 bg-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Navigation size={24} className="animate-pulse" />
            </div>
            <div>
              <h3 className="font-black text-xl font-serif">Workshop Logistics</h3>
              <p className="text-[10px] text-amber-200/40 uppercase font-black tracking-[0.3em]">Update Project Status</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 relative z-10">
            {[RequestStatus.EN_ROUTE, RequestStatus.ARRIVED].map(status => (
              <button
                key={status}
                onClick={() => handleUpdateStatus(status)}
                className={`py-5 px-4 rounded-xl text-[10px] font-black uppercase tracking-[0.3em] transition-all border-b-4 ${
                  request.status === status 
                    ? 'bg-amber-600 text-white border-amber-900 shadow-[0_0_20px_rgba(217,119,6,0.5)]' 
                    : 'bg-stone-800 text-stone-500 border-stone-950 hover:bg-stone-700'
                }`}
              >
                {status}
              </button>
            ))}
            <button
              onClick={() => handleUpdateStatus(RequestStatus.COMPLETED)}
              className="col-span-2 py-5 px-4 rounded-xl text-xs font-black uppercase tracking-[0.4em] bg-emerald-700 text-white border-b-4 border-emerald-950 hover:bg-emerald-600 transition-colors shadow-xl"
            >
              Sign Off Completion
            </button>
          </div>
        </div>
      )}

      {/* Final Review (Requester) */}
      {request.status === RequestStatus.COMPLETED && isOwner && (
        <div className="bg-amber-50 border-4 border-amber-100 p-10 rounded-[2.5rem] space-y-8 shadow-inner animate-shimmer">
          <div className="text-center">
            <h3 className="text-3xl font-black text-amber-950 font-serif">Project Concluded</h3>
            <p className="text-sm font-bold text-amber-800/60 uppercase tracking-widest mt-2">Rate the Master's Handiwork</p>
          </div>
          
          <div className="flex justify-center gap-3">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setRating(s)} className="p-1 hover:scale-125 transition-transform">
                <Star size={40} className={rating >= s ? 'text-amber-500 fill-amber-500' : 'text-amber-200'} />
              </button>
            ))}
          </div>
          
          <textarea 
            className="w-full h-32 p-6 bg-white border border-amber-200 rounded-3xl text-sm font-medium outline-none focus:ring-4 focus:ring-amber-100 resize-none shadow-sm"
            placeholder="Describe the quality of the craft..."
            value={review}
            onChange={(e) => setReview(e.target.value)}
          />
          
          <button 
            onClick={() => navigate('/requester')}
            className="w-full bg-amber-800 text-amber-50 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.3em] hover:bg-amber-900 transition-all border-b-4 border-black/40 shadow-xl"
          >
            Submit Ledger Review
          </button>
        </div>
      )}

      {/* Public Q&A Thread - Journal Style */}
      <div className="space-y-6 pt-8">
        <h3 className="text-xl font-black flex items-center gap-3 text-[#2d1b0f] px-2 font-serif">
          <MessageSquare size={22} className="text-amber-700" />
          Workshop Journal
        </h3>
        
        <div className="space-y-6">
          {requestQuestions.length === 0 ? (
            <div className="bg-white/40 p-12 text-center rounded-[2.5rem] border-2 border-dashed border-stone-200">
              <p className="text-[10px] text-stone-300 font-black uppercase tracking-[0.4em]">Journal is empty</p>
            </div>
          ) : (
            requestQuestions.map(q => (
              <div key={q.id} className="reveal-delay-1">
                <div className="bg-white rounded-[2rem] border border-stone-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[9px] font-black uppercase text-amber-800/40 tracking-[0.2em]">Master Query</span>
                      <span className="text-[10px] font-bold text-stone-400">{q.artisanName}</span>
                    </div>
                    <p className="text-stone-800 font-bold italic leading-relaxed text-base">"{q.question}"</p>
                  </div>
                  
                  {q.answer ? (
                    <div className="p-6 bg-amber-50/30 border-t border-stone-50">
                      <span className="text-[9px] font-black uppercase text-amber-700 tracking-[0.2em] block mb-2">Requester Response</span>
                      <p className="text-stone-600 font-medium">{q.answer}</p>
                    </div>
                  ) : isOwner ? (
                    <div className="p-6 bg-[#faf7f2] border-t border-stone-100">
                      {activeQuestionId === q.id ? (
                        <form onSubmit={(e) => handlePostAnswer(e, q.id)} className="flex gap-3">
                          <input
                            autoFocus
                            className="flex-1 bg-white border border-stone-200 rounded-xl px-5 py-4 text-sm font-medium outline-none focus:ring-4 focus:ring-amber-100"
                            placeholder="Type your response..."
                            value={answerText}
                            onChange={(e) => setAnswerText(e.target.value)}
                          />
                          <button className="bg-amber-800 text-white p-4 rounded-xl shadow-lg hover:bg-amber-900 transition-colors">
                            <Send size={20} />
                          </button>
                        </form>
                      ) : (
                        <button 
                          onClick={() => setActiveQuestionId(q.id)}
                          className="w-full py-4 bg-white border border-stone-200 rounded-xl text-[10px] font-black uppercase tracking-[0.3em] text-amber-800 hover:border-amber-400 transition-all shadow-sm"
                        >
                          Respond to Inquiry
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-stone-50/50 text-center border-t border-stone-100">
                      <span className="text-[9px] font-black text-stone-300 uppercase tracking-widest italic">Inquiry Pending</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {isArtisan && !isOwner && request.status !== RequestStatus.LOCKED && (
          <form onSubmit={handlePostQuestion} className="flex gap-3 bg-white p-4 rounded-3xl border-2 border-stone-100 shadow-xl mt-8">
            <input
              className="flex-1 px-4 py-3 text-sm font-medium outline-none placeholder:text-stone-300"
              placeholder="Ask a clarifying question to the client..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
            />
            <button 
              type="submit" 
              className="bg-amber-800 text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:bg-amber-900 transition-all active:scale-95 border-b-4 border-black/30"
            >
              Post Inquiry
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default RequestDetail;
